import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["action", "autoComplete", "children", "noValidate", "onKeyDown", "onSubmit"];
import { useEffect, useRef } from 'react';
import devLogger from 'react-utils/devLogger';
import invariant from 'react-utils/invariant';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import styled from 'foundations-theming/utils';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useFloatingFormContext } from '../FloatingForm/FloatingFormProvider';
import { useModalPanelContext } from '../ModalPanel/ModalPanelProvider';
import { mergeOverrides } from '../../../utils/mergeOverrides';
import { jsx as _jsx } from "react/jsx-runtime";
let hasPingedNewRelic = false;
const StyledForm = styled.form.withConfig({
  displayName: "Form__StyledForm",
  componentId: "sc-1linwf-0"
})(({
  $cssStyles,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({}, $cssStyles), $styleOverrides);
});
export const Form = _ref => {
  var _floatingFormContext$, _modalPanelContext$fo;
  let {
      action,
      autoComplete,
      children,
      noValidate,
      onKeyDown,
      onSubmit
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const formRef = useRef(null);
  useEffect(() => {
    if (!formRef.current) return;
    const childForms = formRef.current.querySelectorAll('form');
    const nestedFormsExist = childForms.length > 0;
    if (nestedFormsExist) {
      if (window.newrelic && !hasPingedNewRelic) {
        window.newrelic.addPageAction('componentWithNestedForm', {
          componentName: 'Form'
        });
        hasPingedNewRelic = true;
      }
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          message: `A \`<form />\` was rendered within a \`Form\`. Please refactor.`,
          url: 'https://git.hubteam.com/HubSpot/UIComponents/issues/9507',
          key: 'Form-nested'
        });
        invariant(nestedFormsExist, 'A form cannot be nested in a form.');
      }
    }
  }, [formRef]);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const floatingFormContext = useFloatingFormContext();
  const modalPanelContext = useModalPanelContext();
  const sharedStyleOverrides = mergeOverrides((_floatingFormContext$ = floatingFormContext === null || floatingFormContext === void 0 ? void 0 : floatingFormContext.formStylesOverrides) !== null && _floatingFormContext$ !== void 0 ? _floatingFormContext$ : {}, (_modalPanelContext$fo = modalPanelContext === null || modalPanelContext === void 0 ? void 0 : modalPanelContext.formStylesOverrides) !== null && _modalPanelContext$fo !== void 0 ? _modalPanelContext$fo : {});
  return /*#__PURE__*/_jsx(StyledForm, Object.assign({
    ref: formRef,
    action: action,
    $cssStyles: cssStyles,
    $styleOverrides: sharedStyleOverrides,
    onKeyDown: evt => {
      // Submit on Cmd+Enter or Ctrl+Enter (avoiding double-submit from an <input>: #4578)
      const inputFocused = document.activeElement instanceof HTMLInputElement;
      if (evt.key === 'Enter') {
        if (evt.metaKey || evt.ctrlKey && !inputFocused) {
          // @ts-expect-error 'SyntheticEventClass<null>' is not assignable to parameter of type 'FormEvent<HTMLFormElement>'
          if (onSubmit) onSubmit(syntheticEvent(null));
        }
      }
      if (onKeyDown) onKeyDown(evt);
    },
    onSubmit: !action && !onSubmit ? evt => evt && evt.preventDefault() : onSubmit,
    noValidate: noValidate,
    autoComplete: autoComplete
  }, domProps, {
    children: children
  }));
};